import { EditOutlined, UserAddOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareNumeric, compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  ListResponse,
  ManagedSpeakerRow,
  NewLocalSpeakerResponse,
  NotificationsResponse,
  SpeakerSpeechSelectionRow,
} from "../api/types";
import { CountrySelect } from "../components/CountrySelect";
import { DataTable } from "../components/DataTable";

const API_DATE = "YYYY-MM-DD";

interface NewSpeakerFormValues {
  first_name: string;
  last_name: string;
  email: string;
  birthday?: dayjs.Dayjs;
  baptism?: dayjs.Dayjs;
}

interface SpeakerFormValues {
  external: boolean;
  wt_conductor: boolean;
  wt_assistant: boolean;
  simultaneous_absence_limit: boolean;
  phone?: string;
  mobile?: string;
  private_email?: string;
  zip_code?: string;
  country?: string;
}

/**
 * Sidebar entry "speaker_management" → /speaker/manage.
 *
 * The speakers of the own congregation: their flags, and which talks they
 * offer. Contact data is only editable while a speaker has no account of their
 * own — once they have registered they maintain it themselves, which is the
 * rule the old application encoded by making those cells read-only.
 */
export function SpeakerManagementPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SpeakerFormValues>();

  const [newSpeakerForm] = Form.useForm<NewSpeakerFormValues>();

  const [editing, setEditing] = useState<ManagedSpeakerRow | null>(null);
  const [speechesOf, setSpeechesOf] = useState<ManagedSpeakerRow | null>(null);
  const [creating, setCreating] = useState(false);

  const speakers = useQuery({
    queryKey: ["speakers"],
    queryFn: () => apiFetch<ListResponse<ManagedSpeakerRow>>("/speakers"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const save = useMutation({
    mutationFn: (values: SpeakerFormValues) =>
      apiFetch<NotificationsResponse>(`/speakers/${editing!.id}`, {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      // The conductor and assistant flags live on the congregation, so setting
      // one on a speaker clears it on whoever held it before.
      void queryClient.invalidateQueries({ queryKey: ["speakers"] });
    },
    onError: showError,
  });

  // Creates the account together with the role, for a speaker who cannot
  // register themselves — the congregation maintains their data until they do.
  const createSpeaker = useMutation({
    mutationFn: (values: NewSpeakerFormValues) =>
      apiFetch<NewLocalSpeakerResponse>("/roles/new_local_speaker", {
        method: "POST",
        body: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          birthday: values.birthday?.format(API_DATE),
          baptism: values.baptism?.format(API_DATE),
        },
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setCreating(false);
      void queryClient.invalidateQueries({ queryKey: ["speakers"] });
    },
    onError: showError,
  });

  const openEditor = useCallback(
    (row: ManagedSpeakerRow) => {
      setEditing(row);
      // resetFields before filling. The store no longer drops its values when
      // the dialog closes, so a field this handler does not write would keep
      // the previously edited row's value.
      form.resetFields();
      form.setFieldsValue({
        external: row.external,
        wt_conductor: row.wt_conductor,
        wt_assistant: row.wt_assistant,
        simultaneous_absence_limit: row.simultaneous_absence_limit,
        // Contact data belongs to a registered speaker, who maintains it
        // themselves; the dialog hides those fields then. Leaving them out of
        // the form keeps them out of the payload as well, which the dropped
        // preserve={false} used to take care of.
        ...(row.not_registered
          ? {
              phone: row.phone ?? "",
              mobile: row.mobile ?? "",
              private_email: row.private_email ?? "",
              zip_code: row.zip_code ?? "",
              country: row.country ?? "",
            }
          : {}),
      });
    },
    [form],
  );

  const columns = useMemo<TableProps<ManagedSpeakerRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "first_name",
        width: 150,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.first_name, b.first_name),
      },
      {
        title: t("activerecord.attributes.speaker.last_name"),
        dataIndex: "last_name",
        width: 150,
        sorter: (a, b) => compareText(a.last_name, b.last_name),
        render: (value: string | null, row) =>
          row.not_registered ? (
            <Space size={4}>
              {value}
              <Tooltip title={t("speaker.not_registered")}>
                <Tag color="orange">{t("speaker.not_registered")}</Tag>
              </Tooltip>
            </Space>
          ) : (
            value
          ),
      },
      {
        title: t("activerecord.attributes.speaker.external"),
        dataIndex: "external",
        width: 90,
        render: (value: boolean) => (value ? <Tag>{t("yes")}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.speaker.conductor_wt"),
        dataIndex: "wt_conductor",
        width: 100,
        render: (value: boolean) => (value ? <Tag color="blue">{t("yes")}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.speaker.substitute_wt"),
        dataIndex: "wt_assistant",
        width: 120,
        render: (value: boolean) => (value ? <Tag color="blue">{t("yes")}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.speaker.simultaneous_absence_limit"),
        dataIndex: "simultaneous_absence_limit",
        width: 150,
        render: (value: boolean) => (value ? <Tag>{t("yes")}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.speaker.min_weeks_without_assignations"),
        dataIndex: "min_weeks_without_assignations",
        width: 120,
        align: "right",
      },
      {
        title: t("activerecord.attributes.user.zip_code"),
        dataIndex: "zip_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.user.country"),
        dataIndex: "country",
        width: 80,
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "phone",
        width: 140,
      },
      {
        title: t("activerecord.attributes.user.mobile"),
        dataIndex: "mobile",
        width: 140,
      },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "private_email",
        width: 200,
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 70,
        render: (_value, row) => (
          <Tooltip title={t("form.update")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(event) => {
                // The row itself opens the speeches.
                event.stopPropagation();
                openEditor(row);
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [t, openEditor],
  );

  return (
    <Card
      title={t("navigation.speaker_management")}
      extra={
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => {
            newSpeakerForm.resetFields();
            setCreating(true);
          }}
        >
          {t("speaker.new_auxiliary_speaker")}
        </Button>
      }
    >
      <DataTable<ManagedSpeakerRow>
        columns={columns}
        rows={speakers.data?.data}
        exportAs={t("navigation.speaker_management")}
        loading={speakers.isPending}
        error={speakers.error}
        onRowClick={setSpeechesOf}
      />

      <Modal
        open={editing !== null}
        title={[editing?.first_name, editing?.last_name].filter(Boolean).join(" ")}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<SpeakerFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Form.Item name="external" valuePropName="checked">
            <Checkbox>{t("activerecord.attributes.speaker.external")}</Checkbox>
          </Form.Item>

          <Form.Item name="wt_conductor" valuePropName="checked">
            <Checkbox>{t("activerecord.attributes.speaker.conductor_wt")}</Checkbox>
          </Form.Item>

          <Form.Item name="wt_assistant" valuePropName="checked">
            <Checkbox>{t("activerecord.attributes.speaker.substitute_wt")}</Checkbox>
          </Form.Item>

          <Form.Item name="simultaneous_absence_limit" valuePropName="checked">
            <Checkbox>
              {t("activerecord.attributes.speaker.simultaneous_absence_limit")}
            </Checkbox>
          </Form.Item>

          {editing?.not_registered ? (
            <>
              <Form.Item name="zip_code" label={t("activerecord.attributes.user.zip_code")}>
                <Input style={{ width: 140 }} />
              </Form.Item>
              <Form.Item name="country" label={t("activerecord.attributes.user.country")}>
                <CountrySelect style={{ width: 240 }} />
              </Form.Item>
              <Form.Item name="phone" label={t("activerecord.attributes.user.phone")}>
                <Input />
              </Form.Item>
              <Form.Item name="mobile" label={t("activerecord.attributes.user.mobile")}>
                <Input />
              </Form.Item>
              <Form.Item
                name="private_email"
                label={t("activerecord.attributes.user.private_email")}
                rules={[{ type: "email" }]}
              >
                <Input />
              </Form.Item>
            </>
          ) : (
            // Not a permission the client invents: once the speaker has an
            // account, this data is theirs to maintain in their own settings.
            <Typography.Text type="secondary">
              {t("local_coordinator.using_user_email")}
            </Typography.Text>
          )}
        </Form>
      </Modal>

      <Modal
        open={creating}
        title={t("speaker.new_auxiliary_speaker")}
        onCancel={() => setCreating(false)}
        okText={t("speaker.create_auxiliary_speaker")}
        okButtonProps={{ loading: createSpeaker.isPending }}
        onOk={() => newSpeakerForm.submit()}
        destroyOnHidden
      >
        <Form<NewSpeakerFormValues>
          form={newSpeakerForm}
          layout="vertical"
          onFinish={(values) => createSpeaker.mutate(values)}
        >
          <Form.Item
            name="first_name"
            label={t("activerecord.attributes.user.first_name")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="last_name"
            label={t("activerecord.attributes.user.last_name")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label={t("activerecord.attributes.user.email")}
            // The account is created with a random password; this is the
            // address the speaker later completes their registration with.
            rules={[{ required: true }, { type: "email" }]}
          >
            <Input />
          </Form.Item>

          {/* Both optional, as in the old form. The two are condensed into
              user_date_id, which tells two speakers of the same name apart —
              so giving at least one of them helps, and giving neither is still
              allowed. */}
          <Form.Item name="birthday" label={t("activerecord.attributes.user.birthday")}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>

          <Form.Item name="baptism" label={t("activerecord.attributes.user.baptism")}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
        </Form>
      </Modal>

      {speechesOf && (
        <SpeechSelectionDrawer speaker={speechesOf} onClose={() => setSpeechesOf(null)} />
      )}
    </Card>
  );
}

/**
 * Which talks a speaker offers. The endpoint answers with every speech that is
 * available to them — their circumscription's language plus whatever the
 * collaborations open up — with the selected ones first.
 */
function SpeechSelectionDrawer({
  speaker,
  onClose,
}: {
  speaker: ManagedSpeakerRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [notesForm] = Form.useForm<{ additional_notes: string }>();
  const [editingNotes, setEditingNotes] = useState<SpeakerSpeechSelectionRow | null>(null);

  const queryKey = ["speakers", speaker.id, "speech_selection"];

  const speeches = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ListResponse<SpeakerSpeechSelectionRow>>(
        `/speakers/${speaker.id}/speech_selection`,
      ),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(
    (data: NotificationsResponse) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
    },
    // queryKey is derived from the speaker, which cannot change while the
    // drawer is open.
    [notify, queryClient, speaker.id],
  );

  // The same endpoint selects and updates: it upserts the speaker_speeches row,
  // which is why passing the notes along is enough to change them.
  const select = useMutation({
    mutationFn: ({ speechId, notes }: { speechId: number; notes?: string | null }) =>
      apiFetch<NotificationsResponse>(`/speakers/${speaker.id}/speeches/${speechId}`, {
        method: "PUT",
        body: { additional_notes: notes ?? null },
      }),
    onSuccess: (data) => {
      setEditingNotes(null);
      refresh(data);
    },
    onError: showError,
  });

  const deselect = useMutation({
    mutationFn: (speechId: number) =>
      apiFetch<NotificationsResponse>(`/speakers/${speaker.id}/speeches/${speechId}`, {
        method: "DELETE",
      }),
    onSuccess: refresh,
    onError: showError,
  });

  const busy = select.isPending || deselect.isPending;

  const columns = useMemo<TableProps<SpeakerSpeechSelectionRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speaker_speech.selected"),
        dataIndex: "selected",
        width: 90,
        render: (selected: boolean, row) => (
          <Checkbox
            checked={selected}
            disabled={busy}
            onChange={(event) =>
              event.target.checked
                ? select.mutate({ speechId: row.id, notes: row.additional_notes })
                : deselect.mutate(row.id)
            }
          />
        ),
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.speaker_speech.additional_notes"),
        dataIndex: "additional_notes",
        width: 240,
        ellipsis: true,
        render: (notes: string | null, row) =>
          row.selected ? (
            <Space>
              <Typography.Text ellipsis>{notes}</Typography.Text>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={busy}
                onClick={() => {
                  setEditingNotes(row);
                  notesForm.resetFields();
                  notesForm.setFieldsValue({ additional_notes: notes ?? "" });
                }}
              />
            </Space>
          ) : null,
      },
    ],
    [t, busy, select, deselect, notesForm],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={960}
      destroyOnHidden
      title={[speaker.first_name, speaker.last_name].filter(Boolean).join(" ")}
    >
      <DataTable<SpeakerSpeechSelectionRow>
        columns={columns}
        rows={speeches.data?.data}
        exportAs={[speaker.first_name, speaker.last_name, t("activerecord.attributes.speaker.speeches")]
          .filter(Boolean)
          .join(" · ")}
        loading={speeches.isPending || busy}
        error={speeches.error}
      />

      <Modal
        open={editingNotes !== null}
        title={t("activerecord.attributes.speaker_speech.additional_notes")}
        onCancel={() => setEditingNotes(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: select.isPending }}
        onOk={() => notesForm.submit()}
        destroyOnHidden
      >
        <Form
          form={notesForm}
          layout="vertical"
          onFinish={({ additional_notes: notes }) =>
            select.mutate({ speechId: editingNotes!.id, notes })
          }
        >
          <Typography.Paragraph type="secondary">
            {[editingNotes?.schema, editingNotes?.title].filter(Boolean).join(" – ")}
          </Typography.Paragraph>

          <Form.Item name="additional_notes">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
}
